export type JournalPhraseVisibility = 'private' | 'shared';

export interface PhraseMatchInput {
  shortcut: string;
  title?: string | null;
}

export interface SlashTrigger {
  /** Index of the triggering `/`. */
  start: number;
  /** Index after the slash token (before following whitespace or end). */
  end: number;
  /** Text between `/` and the caret. */
  query: string;
}

function isWhitespace(ch: string): boolean {
  return ch === ' ' || ch === '\n' || ch === '\r' || ch === '\t';
}

/**
 * `/` opens a phrase popup when it is at the start of a line or after
 * whitespace. The token runs until the next whitespace.
 */
export function detectSlashTrigger(text: string, cursor: number): SlashTrigger | null {
  if (cursor < 1 || cursor > text.length) return null;

  let slash = cursor - 1;
  while (slash >= 0 && text[slash] !== '/' && !isWhitespace(text[slash])) {
    slash -= 1;
  }
  if (slash < 0 || text[slash] !== '/') return null;
  if (slash > 0 && !isWhitespace(text[slash - 1])) return null;

  let end = cursor;
  while (end < text.length && !isWhitespace(text[end])) {
    end += 1;
  }

  return {
    start: slash,
    end,
    query: text.slice(slash + 1, cursor),
  };
}

export function filterJournalPhrases<T extends PhraseMatchInput>(phrases: T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  const scored = phrases
    .map((phrase) => {
      const shortcut = phrase.shortcut.toLowerCase();
      const title = (phrase.title ?? '').toLowerCase();
      let score = -1;
      if (!needle) {
        score = 0;
      } else if (shortcut === needle) {
        score = 3;
      } else if (shortcut.startsWith(needle)) {
        score = 2;
      } else if (shortcut.includes(needle) || title.includes(needle)) {
        score = 1;
      }
      return { phrase, score, shortcut };
    })
    .filter((row) => row.score >= 0);

  scored.sort((a, b) => b.score - a.score || a.shortcut.localeCompare(b.shortcut, 'da'));
  return scored.map((row) => row.phrase);
}

export function searchJournalPhrases<T extends PhraseMatchInput & { body: string }>(
  phrases: T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return phrases;
  return phrases.filter((phrase) => {
    const haystack = `${phrase.shortcut} ${phrase.title ?? ''} ${phrase.body}`.toLowerCase();
    return haystack.includes(needle);
  });
}

export function insertPhraseText(
  text: string,
  trigger: SlashTrigger,
  body: string,
): { text: string; cursor: number } {
  const next = text.slice(0, trigger.start) + body + text.slice(trigger.end);
  return { text: next, cursor: trigger.start + body.length };
}

export function canManageJournalPhrase(
  phrase: { created_by: string },
  currentUserId: string | null | undefined,
  isAdmin: boolean,
): boolean {
  return phrase.created_by === currentUserId || isAdmin;
}

export interface PhraseAccessInput {
  created_by: string;
  visibility: string;
}

/**
 * Own phrases (private or shared) plus anyone's shared phrases.
 * Other users' private phrases stay out — including for clinic/platform
 * admins. Those rows belong on the settings page only.
 */
export function isOwnOrSharedJournalPhrase(
  phrase: PhraseAccessInput,
  currentUserId: string | null | undefined,
): boolean {
  return phrase.created_by === currentUserId || phrase.visibility === 'shared';
}

export function ownOrSharedJournalPhrases<T extends PhraseAccessInput>(
  phrases: T[],
  currentUserId: string | null | undefined,
): T[] {
  return phrases.filter((phrase) => isOwnOrSharedJournalPhrase(phrase, currentUserId));
}

/** Slash-popup list. Admin vs non-admin use the same own+shared rule. */
export function phrasesForSlashPopup<T extends PhraseAccessInput>(
  phrases: T[],
  currentUserId: string | null | undefined,
): T[] {
  return ownOrSharedJournalPhrases(phrases, currentUserId);
}

export type PhraseVisibilityFilter = 'all' | JournalPhraseVisibility;

export function filterPhrasesByCreatorAndVisibility<T extends PhraseAccessInput>(
  phrases: T[],
  creatorId: string,
  visibility: PhraseVisibilityFilter,
): T[] {
  return phrases.filter((phrase) => {
    if (creatorId !== 'all' && phrase.created_by !== creatorId) return false;
    switch (visibility) {
      case 'all':
        return true;
      case 'private':
        return phrase.visibility === 'private';
      case 'shared':
        return phrase.visibility === 'shared';
      default: {
        const _exhaustive: never = visibility;
        return _exhaustive;
      }
    }
  });
}

export function isPhraseShortcutConflict(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const record = error as { code?: unknown; message?: unknown };
  if (record.code === '23505') return true;
  const message = typeof record.message === 'string' ? record.message.toLowerCase() : '';
  return (
    message.includes('journal_phrases_private_shortcut') ||
    message.includes('journal_phrases_shared_shortcut') ||
    message.includes('duplicate key')
  );
}

export function phraseVisibilityLabel(
  visibility: JournalPhraseVisibility,
  labels: { private: string; shared: string },
): string {
  switch (visibility) {
    case 'private':
      return labels.private;
    case 'shared':
      return labels.shared;
    default: {
      const _exhaustive: never = visibility;
      return _exhaustive;
    }
  }
}
